import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

import type { DriverActionKind } from "@storyboard/dispatch-view/fieldKinds";
import { CORE_FIELD_KINDS, enumValueFor } from "@storyboard/dispatch-view/fieldKinds";
import { type DriverActionContext, driverActionGuidance } from "@/hooks/dispatch/driverActions";
import { zinc } from "@/ui/tokens";
import { DISPATCH_COLOR_VALUES } from "./dispatchColors";
import { EnumPill } from "./FieldValue";

/** The label the registry gives an action, the kind itself when it knows none. */
function actionLabel(action: DriverActionKind): string {
  return enumValueFor(CORE_FIELD_KINDS.driver_action, action)?.label ?? action;
}

/**
 * The hover card over a Driver Action cell: for every action the driver owes, what is wrong and why it matters, then
 * the words to tell the driver, as the context panel's Storyboard status section words them.
 */
function DriverActionGuidanceCard({
  actions,
  context,
}: {
  actions: readonly DriverActionKind[];
  context: DriverActionContext;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, py: 0.5 }}>
      {actions.map((action) => {
        const guidance = driverActionGuidance(action, context);
        return (
          <Box key={action} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box component="span" sx={{ color: DISPATCH_COLOR_VALUES.orange, fontWeight: 600 }}>
              {actionLabel(action)}
            </Box>
            <Box component="span" sx={{ color: zinc[300] }}>
              {guidance.summary}
            </Box>
            <Box component="span">
              <Box component="span" sx={{ color: zinc[400] }}>
                Tell the driver:{" "}
              </Box>
              {guidance.instruction}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * The Driver Action cell: the driver's most pressing action as a pill, and a `+N` count when they owe more. Hovering
 * the cell explains every action and what to tell the driver; a driver who owes none shows the pill alone.
 *
 * @param value - The projected `driver_action` value: the first action, or `working`.
 * @param actions - Every action the driver owes, most pressing first.
 * @param context - The driver's missing permissions and whether their device is checking in, which pick the hover's wording.
 * @param paint - The view's color for this value, when it names one.
 */
export function DriverActionCell({
  value,
  actions,
  context,
  paint,
}: {
  value: string;
  actions: readonly DriverActionKind[];
  context: DriverActionContext;
  paint?: string;
}) {
  const more = actions.length - 1;
  const cell = (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <EnumPill value={value} known={enumValueFor(CORE_FIELD_KINDS.driver_action, value)} paint={paint} />
      {more > 0 && (
        <Box
          component="span"
          aria-label={`${more} more ${more === 1 ? "action" : "actions"}`}
          sx={{
            px: 0.75,
            height: 20,
            display: "inline-flex",
            alignItems: "center",
            borderRadius: 10,
            border: `1px solid ${DISPATCH_COLOR_VALUES.orange}`,
            color: DISPATCH_COLOR_VALUES.orange,
            fontSize: "0.75rem",
            lineHeight: 1,
          }}
        >
          +{more}
        </Box>
      )}
    </Box>
  );
  if (actions.length === 0) return cell;
  return (
    <Tooltip
      title={<DriverActionGuidanceCard actions={actions} context={context} />}
      enterDelay={300}
      placement="bottom-start"
      slotProps={{ tooltip: { sx: { maxWidth: 360, fontSize: "0.8125rem", lineHeight: 1.45 } } }}
    >
      {cell}
    </Tooltip>
  );
}
